import { useState, useRef, forwardRef, useImperativeHandle } from 'react';
import { Modal, Form } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import Axios from '@/shared/api/axios';
import StatusModal from '@/components/ui/StatusModal';
import MessageModal from '@/components/ui/MessageModal';
import LoadingButton from '@/components/ui/LoadingButton';

const API_CONFIG = {
  post: { endpoint: '/post/report', idKey: 'post_id' },
  idol: { endpoint: '/idol/report', idKey: 'idol_id' },
};

/**
 * 檢舉 Modal（貼文／偶像共用）
 *
 * isSubmitting：report 回應較慢，送出後在確認鍵顯示 spinner，
 * 同時鎖住關閉途徑（X／backdrop／ESC／取消），避免使用者以為當掉而重複送出。
 * 沿用 SubscriptionConfirmModal 的 LoadingButton pattern。
 */
const ReportModal = forwardRef(({ targetKey }, ref) => {
  const { t } = useTranslation();
  const [show, setShow] = useState(false);
  const [targetId, setTargetId] = useState(null);
  const [reason, setReason] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [currentStatusCode, setCurrentStatusCode] = useState('');
  const statusModalRef = useRef(null);
  const successModalRef = useRef(null);

  useImperativeHandle(ref, () => ({
    show: (id) => {
      setTargetId(id);
      setReason('');
      setShow(true);
    }
  }));

  const handleClose = () => {
    setShow(false);
    setReason('');
  };

  const handleSubmit = async () => {
    const { endpoint, idKey } = API_CONFIG[targetKey];
    setIsSubmitting(true);
    try {
      const response = await Axios.post(endpoint, { [idKey]: targetId, reason });
      if (response.data.status_code === 'SYSTEM_1000') {
        successModalRef.current?.show();
      } else {
        setCurrentStatusCode(response.data.status_code);
        statusModalRef.current?.show();
      }
    } catch (error) {
      // NETWORK_ERROR 由全域 ServerErrorPage 處理，不另外顯示 modal
      if (error.httpErrorCode && error.httpErrorCode !== 'NETWORK_ERROR') {
        setCurrentStatusCode(error.httpErrorCode);
        statusModalRef.current?.show();
      }
    } finally {
      setIsSubmitting(false);
      handleClose();
    }
  };

  return (
    <>
      <Modal
        show={show}
        onHide={handleClose}
        centered
        backdrop={isSubmitting ? 'static' : true}
        keyboard={!isSubmitting}
      >
        <Modal.Header className="border-bottom pb-3" closeButton={!isSubmitting}>
          <Modal.Title className="fw-bold">{t('report', { target: t(targetKey) })}</Modal.Title>
        </Modal.Header>

        <Modal.Body className="py-4">
          <div>
            <Form.Label className="fw-bold">{t('report_reason')}</Form.Label>
            <Form.Control
              as="textarea"
              rows={2}
              placeholder={t('report_reason_placeholder')}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              disabled={isSubmitting}
            />
          </div>
        </Modal.Body>

        <Modal.Footer className="border-0 d-flex justify-content-end gap-2">
          <button className="btn btn-neutral rounded-pill" onClick={handleClose} disabled={isSubmitting}>
            {t('cancel')}
          </button>
          <LoadingButton
            className="btn btn-primary rounded-pill"
            onClick={handleSubmit}
            disabled={!reason.trim()}
            isLoading={isSubmitting}
          >
            {t('confirm')}
          </LoadingButton>
        </Modal.Footer>
      </Modal>

      <MessageModal ref={successModalRef} status="success">
        <p>{t('report_success')}</p>
      </MessageModal>

      <StatusModal ref={statusModalRef} statusCode={currentStatusCode} />
    </>
  );
});

ReportModal.displayName = 'ReportModal';

export default ReportModal;
